<template>
    <div class="p2p-discussion">
        <div class="p2p-discussion__container">
            <back-link class="back-link" programmatic>
                {{ $t('GO_BACK') }}
            </back-link>

            <h1 class="p2p-discussion__title">{{ title }}</h1>
            <div class="p2p-discussion__content">
                <Discussion v-if="discussionTopic" :topic="discussionTopic" :isPrivate="true" :forceType="'private'"
                    :canCommentAsUser="!!$store.state.customerProfile?.email"
                    :canCommentAsStaff="!!$store.state.publisherProfile?.email" isPurchaseDiscussion />
                <div v-else class="p2p-discussion__loading">
                    {{ $t('LOADING') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Discussion from '@/components/Discussion.vue';
import BackLink from '@/components/BackLink.vue';

export default {
  name: 'P2PDiscussion',
  components: {
    Discussion,
    BackLink,
  },
  data() {
    return {
      discussionTopic: null,
      pollTimeout: null,
      isPolling: false,
    };
  },
  computed: {
    title() {
      return this.$t('P2P_PURCHASE_DISCUSSION');
    },
  },
  methods: {
    async fetchMessages() {
      if (!this.discussionTopic) return;

      try {
        const response = await this.$api.getMessages({
          topic: this.discussionTopic,
          st: this.$store.state.st,
          lt: this.$store.state.lt,
        });

        // Find the Discussion component and update its messages directly
        const discussionComponent = this.$children.find(child => child.$options.name === 'Discussion');
        if (discussionComponent) {
          discussionComponent.messages = response.data || [];
          discussionComponent.messagesLoaded = true;
          discussionComponent.total = response.total || 0;
        }

        // Refresh device info
        await this.$store.dispatch('getDevices');

        // Schedule next poll if we're still polling
        if (this.isPolling) {
          this.pollTimeout = setTimeout(() => {
            this.fetchMessages();
          }, 15000);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        // Stop polling on error
        this.isPolling = false;
        clearTimeout(this.pollTimeout);
      }
    },

    startPolling() {
      this.isPolling = true;
      this.fetchMessages();
    },

    stopPolling() {
      this.isPolling = false;
      clearTimeout(this.pollTimeout);
    },

    async handleMessageRead(messageId) {
      try {
        await this.$api.post('set_message_read', {
          id: messageId,
          topic: this.discussionTopic,
          st: this.$store.state.st,
          lt: this.$store.state.lt,
        });

        // Refresh device info after marking message as read
        await this.$store.dispatch('getDevices');
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    },
  },
  mounted() {
    // Get the p2p_purchase_id from the route query parameters
    const p2pPurchaseId = this.$route.query.id;
    if (p2pPurchaseId) {
      this.discussionTopic = `p2p_${p2pPurchaseId}`;
    } else {
      // If no ID is provided, redirect to the home page
      this.$router.push({ name: 'home' });
    }

    // Start polling when component is mounted
    this.startPolling();
  },
  beforeDestroy() {
    // Clean up polling when component is destroyed
    this.stopPolling();
  },
};
</script>

<style lang="scss" scoped>
.p2p-discussion {
    padding: 20px;

    &__container {
        position: relative;
        max-width: 900px;
        margin: 0 auto;
        background-color: var(--bg-primary);
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    &__title {
        margin-bottom: 20px;
        font-size: 24px;
        font-weight: 600;
    }

    &__content {
        min-height: 500px;
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 200px;
        color: var(--text-secondary);
    }
}

.back-link {
    display: block;
    margin-bottom: 15px;
    color: var(--text-primary);

    &:hover {
        color: var(--primary);
    }
}
</style>
